import React from 'react';
import {
  Edit,
  SimpleForm,
  TextInput,
  BooleanInput,
  useTranslate,
  useRecordContext,
  Toolbar,
  SaveButton,
  useRedirect,
  required,
  useNotify,
} from 'react-admin';
import { ServerErrorBinder } from '../common/ServerErrorBinder';
import { translateServerMessage } from '../../utils/translateServerMessage';

const PAYMENT_GATEWAY_KNOWN_FIELDS = new Set([
    'name', 'code', 'is_active', 'description', 'api_endpoint',
    'sandbox_mode', 'credentials', 'metadata',
]);

const mapPaymentGatewayBackendPath = (path: string): string =>
    path.replace(/^body\./, '').replace(/^params\./, '').replace(/^query\./, '');

const paymentGatewayFieldLabelKey = (field: string): string => {
    const head = field.split('.')[0];
    return PAYMENT_GATEWAY_KNOWN_FIELDS.has(head)
        ? `resources.payment-gateways.fields.${head}`
        : field;
};
import {
  Box,
  Card,
  CardContent,
  Typography,
  IconButton,
  Chip,
  Alert,
} from '@mui/material';
import {
  ArrowBack,
  Settings,
  Info,
  CheckCircle,
  Warning,
  VpnKey,
  AccountBalanceWallet,
} from '@mui/icons-material';

const EditToolbar = () => {
  return (
    <Toolbar
      sx={{
        backgroundColor: '#FAFAFA',
        borderTop: '1px solid #E0E0E0',
        px: { xs: 2, sm: 3 },
        py: 2,
        mt: { xs: 2, sm: 4 },
        '& .MuiButton-root': {
          borderRadius: 2,
          textTransform: 'none',
          fontWeight: 600,
          px: 3,
          py: 1.5,
        },
      }}
    >
      <SaveButton
        variant="contained"
        sx={{
          backgroundColor: '#00C853',
          '&:hover': {
            backgroundColor: '#00A843',
            boxShadow: '0 4px 12px rgba(0,200,83,0.3)',
          },
        }}
      />
    </Toolbar>
  );
};

const PageHeader = () => {
  const redirect = useRedirect();
  const translate = useTranslate();
  const record = useRecordContext();

  if (!record) return null;

  return (
    <Box sx={{ mb: { xs: 2, sm: 4 } }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 2,
        }}
      >
        <IconButton
          onClick={() => redirect('/payment-gateways')}
          size="large"
          sx={{
            flexShrink: 0,
            backgroundColor: '#FFFFFF',
            boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
            '&:hover': {
              backgroundColor: '#F5F5F5',
              boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
            },
          }}
        >
          <ArrowBack />
        </IconButton>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
            <AccountBalanceWallet sx={{ fontSize: { xs: 24, sm: 32 }, color: '#00C853' }} />
            <Typography
              sx={{
                fontWeight: 600,
                fontSize: { xs: '1.25rem', sm: '2.125rem' },
                wordBreak: 'break-word',
                overflowWrap: 'anywhere',
              }}
            >
              {record.name}
            </Typography>
          </Box>
          <Typography variant="body1" color="text.secondary" sx={{ mt: 0.5 }}>
            {translate('resources.payment-gateways.hints.edit_info')}
          </Typography>
        </Box>
      </Box>
      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1.5, ml: { xs: 0, sm: 7 } }}>
        <Chip
          label={
            record.is_configured
              ? translate('resources.payment-gateways.status.configured')
              : translate('resources.payment-gateways.status.not_configured')
          }
          color={record.is_configured ? 'success' : 'warning'}
          icon={record.is_configured ? <CheckCircle /> : <Warning />}
          size="small"
        />
        {record.supports_tokenization && (
          <Chip
            label={translate('resources.payment-gateways.fields.supports_tokenization')}
            color="info"
            size="small"
          />
        )}
      </Box>
    </Box>
  );
};

const ConfigurationWarning = () => {
  const record = useRecordContext();
  const translate = useTranslate();

  if (!record || record.is_configured) return null;

  return (
    <Alert severity="warning" sx={{ mb: { xs: 2, sm: 3 } }}>
      <Typography variant="body2">
        {translate('resources.payment-gateways.hints.not_configured_warning')}
      </Typography>
    </Alert>
  );
};

const GatewayInfoFields = () => {
  const record = useRecordContext();
  const translate = useTranslate();

  if (!record) return null;

  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: { xs: 2, sm: 3 } }}>
      <Box sx={{ minWidth: 120 }}>
        <Typography variant="caption" color="text.secondary">
          {translate('resources.payment-gateways.fields.code')}
        </Typography>
        <Typography variant="body2" fontWeight={600} sx={{ textTransform: 'uppercase' }}>
          {record.code}
        </Typography>
      </Box>
      <Box sx={{ minWidth: 120 }}>
        <Typography variant="caption" color="text.secondary">
          {translate('resources.payment-gateways.fields.supports_tokenization')}
        </Typography>
        <Typography variant="body2" fontWeight={500}>
          {record.supports_tokenization ? translate('common.yes') : translate('common.no')}
        </Typography>
      </Box>
      <Box sx={{ minWidth: 120 }}>
        <Typography variant="caption" color="text.secondary">
          {translate('resources.payment-gateways.fields.env_var_prefix')}
        </Typography>
        <Typography variant="body2" fontWeight={500} fontFamily="monospace">
          {record.env_var_prefix || '-'}
        </Typography>
      </Box>
    </Box>
  );
};

const EnvironmentInfo = () => {
  const record = useRecordContext();
  const translate = useTranslate();

  if (!record) return null;

  const prefix = record.env_var_prefix;
  const envVars = prefix
    ? [
        `${prefix}_ENABLED`,
        `${prefix}_ENV`,
        `${prefix}_MERCHANT_ID`,
        `${prefix}_PRIVATE_KEY`,
        `${prefix}_PUBLIC_KEY`,
      ]
    : [];

  return (
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: { xs: 1.5, sm: 2 } }}>
        {translate('resources.payment-gateways.hints.environment_vars')}
      </Typography>

      {prefix ? (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          {envVars.map((envVar) => (
            <Chip
              key={envVar}
              label={envVar}
              size="small"
              variant="outlined"
              sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}
            />
          ))}
        </Box>
      ) : (
        <Typography variant="body2" color="text.secondary">
          {translate('resources.payment-gateways.hints.no_env_prefix')}
        </Typography>
      )}
    </Box>
  );
};

export const PaymentGatewaysEdit = () => {
  const translate = useTranslate();
  const notify = useNotify();
  const [serverErrors, setServerErrors] = React.useState<Record<string, string>>({});

  return (
    <Edit
      title={false}
      component="div"
      redirect="list"
      mutationMode="pessimistic"
      actions={false}
      mutationOptions={{
        onSuccess: () => {
          setServerErrors({});
          notify(translate('ra.notification.updated', { smart_count: 1 }), { type: 'success' });
        },
        onError: (error: any) => {
          const apiError = error?.body || error;
          const rawErrors = apiError?.errors as Record<string, string[] | string> | undefined;
          const fieldErrors: Record<string, string> = {};
          const summaryLines: string[] = [];
          if (rawErrors) {
            for (const [path, msgs] of Object.entries(rawErrors)) {
              const rawMsg = Array.isArray(msgs) ? msgs[0] : String(msgs);
              const translated = translateServerMessage(rawMsg, translate);
              const field = mapPaymentGatewayBackendPath(path);
              fieldErrors[field] = translated;
              const label = translate(paymentGatewayFieldLabelKey(field), { _: field });
              summaryLines.push(`• ${label}: ${translated}`);
            }
          }
          setServerErrors(fieldErrors);
          const headerKey = apiError?.errorCode ? `errors.${apiError.errorCode}` : 'validation.formHasErrors';
          const header = translate(headerKey, { _: apiError?.message || 'Error' });
          if (summaryLines.length > 0) {
            notify(`${header}\n${summaryLines.join('\n')}`, { type: 'error', multiLine: true, autoHideDuration: 10000 });
          } else {
            notify(header, { type: 'error', autoHideDuration: 6000 });
          }
        },
      }}
      sx={{
        backgroundColor: 'transparent',
        '& .RaEdit-main': {
          backgroundColor: 'transparent',
          boxShadow: 'none',
        },
      }}
    >
      <SimpleForm
        toolbar={<EditToolbar />}
        sx={{
          '& .MuiPaper-root': {
            backgroundColor: 'transparent',
            boxShadow: 'none',
          },
          '& > .MuiCardContent-root, & > div > .MuiCardContent-root': {
            px: { xs: 0, sm: 2 },
            py: { xs: 1, sm: 2 },
          },
          padding: { xs: 0, sm: 'inherit' },
        }}
      >
        <ServerErrorBinder errors={serverErrors} />
        <Box
          sx={{
            width: '100%',
            maxWidth: 800,
            margin: '0 auto',
            '& .MuiFormControl-root': { width: '100%' },
            pb: { xs: 10, sm: 2 },
          }}
        >
          <PageHeader />
          <ConfigurationWarning />

          {/* Basic Settings Card */}
          <Card sx={{ mb: { xs: 2, sm: 3 }, overflow: 'hidden' }}>
            <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <Settings sx={{ mr: 2, color: '#00C853' }} />
                <Typography variant="h6" sx={{ fontWeight: 600, fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                  {translate('resources.payment-gateways.sections.basic_settings')}
                </Typography>
              </Box>

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Box sx={{ '& .MuiFormControl-root': { marginTop: 0, width: '100%' } }}>
                  <TextInput
                    source="name"
                    label={translate('resources.payment-gateways.fields.name')}
                    validate={[required()]}
                    fullWidth
                  />
                </Box>
                <Box sx={{ '& .MuiFormControl-root': { marginTop: 0 } }}>
                  <BooleanInput
                    source="is_active"
                    label={translate('resources.payment-gateways.fields.is_active')}
                    helperText={translate('resources.payment-gateways.hints.is_active')}
                  />
                </Box>
              </Box>
            </CardContent>
          </Card>

          {/* Gateway Information Card */}
          <Card sx={{ mb: { xs: 2, sm: 3 }, backgroundColor: '#FAFAFA', overflow: 'hidden' }}>
            <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <Info sx={{ mr: 2, color: '#757575' }} />
                <Typography variant="h6" sx={{ fontWeight: 600, color: '#757575', fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                  {translate('resources.payment-gateways.sections.gateway_info')}
                </Typography>
              </Box>

              <GatewayInfoFields />
            </CardContent>
          </Card>

          {/* Environment Variables Card */}
          <Card sx={{ overflow: 'hidden' }}>
            <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <VpnKey sx={{ mr: 2, color: '#00C853' }} />
                <Typography variant="h6" sx={{ fontWeight: 600, fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                  {translate('resources.payment-gateways.sections.environment')}
                </Typography>
              </Box>

              <EnvironmentInfo />
            </CardContent>
          </Card>
        </Box>
      </SimpleForm>
    </Edit>
  );
};
